import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Navbar } from "../../components/Layout";
import { Trash2, Plus, ArrowRight, ExternalLink } from "lucide-react";

export default function RedirectsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ from_path: "", to_path: "", status_code: 301 });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/redirects").then(r => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/admin/redirects", form);
      setForm({ from_path: "", to_path: "", status_code: 301 });
      load();
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Erreur de création");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette redirection ?")) return;
    await api.delete(`/admin/redirects/${id}`);
    load();
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="redirects-admin">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 md:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/admin" className="text-xs uppercase tracking-widest text-slate-500 font-semibold">← Admin</Link>
            <h1 className="font-display font-black text-3xl md:text-4xl mt-2">Redirections 301</h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
              Quand une URL change (slug renommé, page migrée, ancien lien externe), ajoutez ici
              la redirection. Google Search Console mettra à jour l'index à la prochaine indexation.
            </p>
          </div>
        </div>

        <form onSubmit={create} className="bg-white border-2 border-slate-900 p-6 mb-8 shadow-[4px_4px_0px_#0f172a]" data-testid="redirect-create-form">
          <div className="flex items-center gap-2 mb-4">
            <Plus className="w-5 h-5 text-[#FFC107]" />
            <span className="text-xs uppercase tracking-widest font-bold text-slate-600">Nouvelle redirection</span>
          </div>
          <div className="grid md:grid-cols-12 gap-3">
            <input
              required
              type="text"
              placeholder="/ancienne-url"
              value={form.from_path}
              onChange={e => setForm(f => ({ ...f, from_path: e.target.value }))}
              className="md:col-span-5 border-2 border-slate-900 px-4 py-3 text-sm font-mono focus:outline-none focus:border-[#FFC107]"
              data-testid="redirect-from-path"
            />
            <div className="md:col-span-1 flex items-center justify-center text-slate-500">
              <ArrowRight className="w-5 h-5" />
            </div>
            <input
              required
              type="text"
              placeholder="/nouvelle-url ou https://..."
              value={form.to_path}
              onChange={e => setForm(f => ({ ...f, to_path: e.target.value }))}
              className="md:col-span-4 border-2 border-slate-900 px-4 py-3 text-sm font-mono focus:outline-none focus:border-[#FFC107]"
              data-testid="redirect-to-path"
            />
            <select
              value={form.status_code}
              onChange={e => setForm(f => ({ ...f, status_code: parseInt(e.target.value) }))}
              className="md:col-span-2 border-2 border-slate-900 px-4 py-3 text-sm font-bold focus:outline-none focus:border-[#FFC107]"
              data-testid="redirect-status-code"
            >
              <option value={301}>301 Permanent</option>
              <option value={302}>302 Temporaire</option>
            </select>
          </div>
          {error && <p className="text-sm text-red-600 mt-3" data-testid="redirect-error">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            className="mt-4 bg-slate-900 text-white px-6 py-3 font-bold uppercase tracking-wider text-sm border-2 border-slate-900 hover:bg-[#FFC107] hover:text-slate-900 transition-colors disabled:opacity-50"
            data-testid="redirect-submit"
          >
            {busy ? "Création..." : "Créer la redirection"}
          </button>
        </form>

        <div className="bg-white border-2 border-slate-900">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b-2 border-slate-900 bg-slate-50 text-xs uppercase tracking-widest font-bold text-slate-600">
            <div className="col-span-5">De</div>
            <div className="col-span-5">Vers</div>
            <div className="col-span-1 text-center">Hits</div>
            <div className="col-span-1 text-right">Action</div>
          </div>
          {loading && <div className="p-8 text-center text-sm text-slate-500">Chargement…</div>}
          {!loading && items.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="redirects-empty">
              Aucune redirection pour l'instant. Ajoutez-en quand vous modifiez un slug ou migrez une URL.
            </div>
          )}
          {!loading && items.map((r) => (
            <div key={r.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-200 items-center text-sm hover:bg-slate-50" data-testid={`redirect-row-${r.id}`}>
              <div className="col-span-5 font-mono text-slate-900 break-all flex items-center gap-2">
                <span className="inline-block px-2 py-0.5 text-[10px] font-bold bg-slate-900 text-white rounded">{r.status_code}</span>
                {r.from_path}
              </div>
              <div className="col-span-5 font-mono text-slate-700 break-all flex items-center gap-1">
                {r.to_path}
                {r.to_path?.startsWith("http") && <ExternalLink className="w-3 h-3 text-slate-400" />}
              </div>
              <div className="col-span-1 text-center font-bold text-slate-900">{r.hits || 0}</div>
              <div className="col-span-1 text-right">
                <button
                  onClick={() => remove(r.id)}
                  className="text-red-600 hover:bg-red-50 p-2"
                  data-testid={`redirect-delete-${r.id}`}
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 bg-blue-50 border-2 border-blue-200 p-5 text-sm text-slate-700">
          <p className="font-bold mb-1">📡 Sitemap.xml</p>
          <p>Votre sitemap est généré automatiquement à partir de la base. URL publique à coller dans Google Search Console :</p>
          <p className="mt-2 font-mono text-xs bg-white border border-slate-300 px-3 py-2 inline-block">
            https://immobiliersolaire.fr/api/sitemap.xml
          </p>
        </div>
      </div>
    </div>
  );
}
